import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Tabs,
  Tab,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Chip,
  Tooltip,
  Alert,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import { 
  EmailOutlined, 
  OpenInNew, 
  TouchApp, 
  CheckCircleOutline,
  TrendingUp,
  Timeline,
  Person
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { 
  PieChart, 
  Pie, 
  Cell, 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip as RechartsTooltip, 
  Legend,
  ResponsiveContainer,
  LineChart,
  Line
} from 'recharts';
import trackingService, { ContactEngagementSummary } from '../../../../services/trackingService';
import DashboardMetricCard from './DashboardMetricCard';
import format from 'date-fns/format';
import { formatDistanceToNow } from 'date-fns';

interface ContactAnalyticsTabProps {
  organizationId: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`analytics-tabpanel-${index}`}
      aria-labelledby={`analytics-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 2 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `analytics-tab-${index}`,
    'aria-controls': `analytics-tabpanel-${index}`,
  };
}

// Custom component for engagement timelines
const EngagementTimeline: React.FC<{ engagements: any[] }> = ({ engagements }) => {
  return (
    <Box sx={{ mt: 2, maxHeight: 400, overflowY: 'auto' }}>
      {engagements.map((engagement, index) => {
        const date = new Date(engagement.date);
        
        // Different colors based on engagement type
        const getColor = () => {
          switch (engagement.type) {
            case 'sent': return '#e0e0e0';
            case 'open': return '#bbdefb';
            case 'click': return '#c8e6c9';
            case 'conversion': return '#ffcc80';
            default: return '#e0e0e0';
          }
        };
        
        // Different icons based on engagement type
        const getIcon = () => {
          switch (engagement.type) {
            case 'sent': return <EmailOutlined fontSize="small" />;
            case 'open': return <OpenInNew fontSize="small" />;
            case 'click': return <TouchApp fontSize="small" />;
            case 'conversion': return <CheckCircleOutline fontSize="small" />;
            default: return <EmailOutlined fontSize="small" />;
          }
        };
        
        return (
          <Box
            key={index}
            sx={{
              display: 'flex',
              mb: 2,
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                left: 18,
                top: 24,
                bottom: -16,
                width: 2,
                backgroundColor: index === engagements.length - 1 ? 'transparent' : '#e0e0e0',
              }
            }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: getColor(),
                zIndex: 1,
              }}
            >
              {getIcon()}
            </Box>
            <Box sx={{ ml: 2, flex: 1 }}>
              <Typography variant="subtitle2">
                {engagement.type.charAt(0).toUpperCase() + engagement.type.slice(1)}
                {' '}
                <Chip
                  size="small"
                  label={engagement.itemType}
                  color={
                    engagement.itemType === 'event' ? 'primary' : 
                    engagement.itemType === 'subscription' ? 'secondary' : 'default'
                  }
                />
              </Typography>
              <Typography variant="body2">{engagement.itemName}</Typography>
              <Typography variant="caption" color="textSecondary">
                {format(date, 'MMM d, yyyy h:mm a')} ({formatDistanceToNow(date, { addSuffix: true })})
              </Typography>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

// Main component
const ContactAnalyticsTab: React.FC<ContactAnalyticsTabProps> = ({ organizationId }) => {
  const theme = useTheme();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [analytics, setAnalytics] = useState<any | null>(null);
  const [tabValue, setTabValue] = useState<number>(0);
  const [selectedContact, setSelectedContact] = useState<string | null>(null);
  const [contactAnalytics, setContactAnalytics] = useState<ContactEngagementSummary | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [refreshTrigger, setRefreshTrigger] = useState<number>(0);

  // Define chart colors
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#A569BD', '#EC7063'];
  
  // Fetch analytics data
  useEffect(() => {
    const fetchAnalytics = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const orgAnalytics = await trackingService.getOrganizationEmailAnalytics(organizationId);
        setAnalytics(orgAnalytics);
        
        // If we have a selected contact, load its detailed analytics
        if (selectedContact) {
          loadContactAnalytics(selectedContact);
        }
      } catch (error) {
        console.error('Error fetching email analytics:', error);
        setError('Failed to load email analytics. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    if (organizationId) {
      fetchAnalytics();
    }
  }, [organizationId, refreshTrigger, selectedContact]);
  
  // Load analytics for a specific contact
  const loadContactAnalytics = async (contactId: string) => {
    try {
      setLoading(true);
      const contactData = await trackingService.getContactAnalytics(organizationId, contactId);
      setContactAnalytics(contactData);
    } catch (error) {
      console.error('Error loading contact analytics:', error);
      setError('Failed to load contact details. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Tab change handler
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    
    // Reset selected contact when going back to overview
    if (newValue === 0) {
      setSelectedContact(null);
      setContactAnalytics(null);
    }
  };
  
  // Table pagination handlers
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };
  
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  // Handle viewing a contact's details
  const handleViewContact = (contactId: string) => {
    setSelectedContact(contactId);
    setTabValue(1); // Switch to individual tab
    loadContactAnalytics(contactId);
  };
  
  // Refresh data
  const handleRefresh = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  // Prepare data for charts
  const prepareEmailTypeData = () => {
    if (!analytics || !analytics.emailsByType) return [];
    
    return Object.entries(analytics.emailsByType).map(([type, count]) => ({
      name: type.charAt(0).toUpperCase() + type.slice(1),
      value: count
    }));
  };
  
  // For the contact table
  const createData = (
    contactName: string,
    contactId: string,
    totalEmails: number,
    openRate: number,
    clickRate: number,
    conversionRate: number,
    lastEngagement: any
  ) => {
    return { contactName, contactId, totalEmails, openRate, clickRate, conversionRate, lastEngagement };
  };
  
  const rows = analytics?.topEngagedContacts
    ? analytics.topEngagedContacts.map((contact: any) => 
        createData(
          contact.contactName,
          contact.contactId,
          contact.totalEmails,
          contact.openRate,
          contact.clickRate,
          contact.conversionRate,
          contact.lastEngagement
        )
      )
    : [];
    
  if (loading && !analytics) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange} 
          aria-label="analytics tabs"
        >
          <Tab label="Overview" {...a11yProps(0)} />
          <Tab 
            label={selectedContact ? "Contact Details" : "Individual Analytics"} 
            {...a11yProps(1)}
            disabled={!selectedContact} 
          />
        </Tabs>
      </Box>
      
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Button 
          variant="outlined" 
          onClick={handleRefresh}
          disabled={loading}
        >
          Refresh Data
        </Button>
      </Box>
      
      <TabPanel value={tabValue} index={0}>
        {/* Overview Dashboard */}
        {loading && !analytics ? (
          <CircularProgress />
        ) : (
          <>
            {/* Metric Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={6} md={3}>
                <DashboardMetricCard
                  title="Total Emails Sent"
                  value={analytics?.totalEmails || 0}
                  icon="email"
                  color="#4caf50"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <DashboardMetricCard
                  title="Average Open Rate"
                  value={`${(analytics?.averageOpenRate || 0).toFixed(1)}%`}
                  icon="visibility"
                  color="#2196f3"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <DashboardMetricCard
                  title="Average Click Rate"
                  value={`${(analytics?.averageClickRate || 0).toFixed(1)}%`}
                  color="#ff9800"
                  icon="touch_app"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <DashboardMetricCard
                  title="Conversion Rate"
                  value={`${(analytics?.averageConversionRate || 0).toFixed(1)}%`}
                  icon="verified"
                  color="#9c27b0"
                />
              </Grid>
            </Grid>
            
            {/* Charts Section */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Email Distribution by Type</Typography>
                    <Box sx={{ height: 300 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={prepareEmailTypeData()}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            outerRadius={100}
                            fill="#8884d8"
                            dataKey="value"
                            label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                          >
                            {prepareEmailTypeData().map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <RechartsTooltip formatter={(value: number) => [value, 'Emails Sent']} />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Engagement Rates</Typography>
                    <Box sx={{ height: 300 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={[
                            {
                              name: 'Open Rate',
                              value: analytics?.averageOpenRate || 0,
                              fill: '#bbdefb'
                            },
                            {
                              name: 'Click Rate',
                              value: analytics?.averageClickRate || 0,
                              fill: '#c8e6c9'
                            },
                            {
                              name: 'Conversion Rate',
                              value: analytics?.averageConversionRate || 0,
                              fill: '#ffcc80'
                            }
                          ]}
                          margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" />
                          <YAxis label={{ value: 'Percentage (%)', angle: -90, position: 'insideLeft' }} />
                          <RechartsTooltip formatter={(value: number) => [`${value.toFixed(1)}%`, 'Rate']} />
                          <Legend />
                          <Bar dataKey="value" name="Rate %" />
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
            
            {/* Top Engaged Contacts Table */}
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Top Engaged Contacts
                </Typography>
                
                <TableContainer component={Paper} elevation={0}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Contact</TableCell>
                        <TableCell align="right">Emails</TableCell>
                        <TableCell align="right">Open Rate</TableCell>
                        <TableCell align="right">Click Rate</TableCell>
                        <TableCell align="right">Conversion Rate</TableCell>
                        <TableCell align="right">Last Engagement</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row: {
                        contactName: string;
                        contactId: string;
                        totalEmails: number;
                        openRate: number;
                        clickRate: number;
                        conversionRate: number;
                        lastEngagement: any;
                      }) => (
                        <TableRow key={row.contactId}>
                          <TableCell component="th" scope="row">
                            {row.contactName}
                          </TableCell>
                          <TableCell align="right">{row.totalEmails}</TableCell>
                          <TableCell align="right">{row.openRate.toFixed(1)}%</TableCell>
                          <TableCell align="right">{row.clickRate.toFixed(1)}%</TableCell>
                          <TableCell align="right">{row.conversionRate.toFixed(1)}%</TableCell>
                          <TableCell align="right">
                            {row.lastEngagement 
                              ? formatDistanceToNow(row.lastEngagement.toDate(), { addSuffix: true })
                              : 'N/A'}
                          </TableCell>
                          <TableCell align="right">
                            <Button
                              size="small"
                              onClick={() => handleViewContact(row.contactId)}
                            >
                              View Details
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                      
                      {rows.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={7} align="center">
                            No engagement data available yet
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                  
                  <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={rows.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                  />
                </TableContainer>
              </CardContent>
            </Card>
          </>
        )}
      </TabPanel>
      
      <TabPanel value={tabValue} index={1}>
        {/* Individual Contact Analytics */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : contactAnalytics ? (
          <Box>
            <Paper elevation={1} sx={{ p: 3, mb: 4 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={9}>
                  <Typography variant="h5" sx={{ mb: 1 }}>
                    <Person sx={{ mr: 1, verticalAlign: 'middle' }} />
                    {analytics?.topEngagedContacts.find(
                      (c: any) => c.contactId === selectedContact
                    )?.contactName || 'Contact Details'}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Contact ID: {contactAnalytics.contactId}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={3} sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
                  <Button 
                    variant="outlined" 
                    onClick={() => {
                      setTabValue(0);
                      setSelectedContact(null);
                    }}
                  >
                    Back to Overview
                  </Button>
                </Grid>
              </Grid>
            </Paper>

            {/* Engagement Metrics */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={6} md={3}>
                <DashboardMetricCard
                  title="Total Emails Received"
                  value={contactAnalytics.totalEmails}
                  icon="email"
                  color="#4caf50"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <DashboardMetricCard
                  title="Open Rate"
                  value={`${contactAnalytics.openRate.toFixed(1)}%`}
                  icon="visibility"
                  color="#2196f3"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <DashboardMetricCard
                  title="Click Rate"
                  value={`${contactAnalytics.clickRate.toFixed(1)}%`}
                  icon="touch_app"
                  color="#ff9800"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <DashboardMetricCard
                  title="Conversion Rate"
                  value={`${contactAnalytics.conversionRate.toFixed(1)}%`}
                  icon="verified"
                  color="#9c27b0"
                />
              </Grid>
            </Grid>

            {/* Detailed Analytics */}
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      <EmailOutlined sx={{ mr: 1, verticalAlign: 'middle' }} />
                      Email Distribution
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    
                    <Box sx={{ height: 300 }}>
                      {Object.keys(contactAnalytics.emailsByType).length > 0 ? (
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={Object.entries(contactAnalytics.emailsByType).map(([type, count]) => ({
                                name: type.charAt(0).toUpperCase() + type.slice(1),
                                value: count
                              }))}
                              cx="50%"
                              cy="50%"
                              labelLine={false}
                              outerRadius={100}
                              fill="#8884d8"
                              dataKey="value"
                              label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                            >
                              {Object.keys(contactAnalytics.emailsByType).map((_, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                              ))}
                            </Pie>
                            <RechartsTooltip formatter={(value: number) => [value, 'Emails']} />
                            <Legend />
                          </PieChart>
                        </ResponsiveContainer>
                      ) : (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                          <Typography variant="body1" color="textSecondary">
                            No email data available
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      <TrendingUp sx={{ mr: 1, verticalAlign: 'middle' }} />
                      Engagement Metrics
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    
                    <Box sx={{ height: 300 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={[
                            {
                              name: 'Opens',
                              value: contactAnalytics.totalOpens,
                              fill: '#bbdefb'
                            },
                            {
                              name: 'Clicks',
                              value: contactAnalytics.totalClicks,
                              fill: '#c8e6c9'
                            },
                            {
                              name: 'Conversions',
                              value: contactAnalytics.totalConversions,
                              fill: '#ffcc80'
                            }
                          ]}
                          margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" />
                          <YAxis />
                          <RechartsTooltip />
                          <Legend />
                          <Bar dataKey="value" name="Count" />
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      <Timeline sx={{ mr: 1, verticalAlign: 'middle' }} />
                      Engagement Timeline
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    
                    {contactAnalytics.engagementTimeline.length > 0 ? (
                      <EngagementTimeline engagements={contactAnalytics.engagementTimeline} />
                    ) : (
                      <Box sx={{ p: 3, textAlign: 'center' }}>
                        <Typography variant="body1" color="textSecondary">
                          No engagement activity recorded yet
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        ) : (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="h6" color="textSecondary">
              Select a contact to view detailed analytics
            </Typography>
          </Box>
        )}
      </TabPanel>
    </Box>
  );
};

export default ContactAnalyticsTab;
